/* Uses Creative's bundled Slick when available, with a native scrolling fallback.
   One controller owns rotation; quote/name/model heights are measured per section. */
if (!customElements.get('dug-testimonials')) {
  customElements.define('dug-testimonials', class extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this._ready = false;
      const start = () => { if (this.isConnected && !this._ready) this._boot(); };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true, signal: this._controller.signal });
      } else {
        this._bootFrame = requestAnimationFrame(start);
      }
    }
    _boot() {
      this._track = this.querySelector('.dug-t-grid');
      if (!this._track) return;
      this._ready = true;
      const signal = this._controller.signal;
      this._isCarousel = this._track.hasAttribute('data-dug-slick');
      this._queueMeasure();
      window.addEventListener('resize', () => this._queueMeasure(), { signal });
      this.addEventListener('load', () => this._queueMeasure(), { capture: true, signal });
      if (document.fonts) {
        document.fonts.ready.then(() => { if (this.isConnected) this._queueMeasure(); });
        document.fonts.addEventListener('loadingdone', () => this._queueMeasure(), { signal });
      }
      if ('ResizeObserver' in window) {
        this._widthObserver = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width;
          if (width && width !== this._lastWidth) {
            this._lastWidth = width;
            this._queueMeasure();
          }
        });
        this._widthObserver.observe(this.querySelector('.dug-t-inner') || this);
      }
      if (!this._isCarousel) return;
      this._count = this._track.querySelectorAll('.dug-t-card').length;
      this._index = 0;
      this._usingSlick = false;
      this._controls = this.querySelector('.dug-t-controls');
      this._toggle = this.querySelector('[data-dug-toggle]');
      this._status = this.querySelector('[data-dug-status]');
      this._motion = matchMedia('(prefers-reduced-motion: reduce)');
      this._manualPaused = this.dataset.autoplay === 'false' || this._motion.matches;
      this._hovered = false;
      this._keyboardFocus = false;
      this._dragging = false;
      this._visible = !('IntersectionObserver' in window);
      this._interval = Math.max(6, Math.min(20, Number(this.dataset.autoplaySeconds) || 10)) * 1000;
      this._controls.hidden = this._count < 2;
      this._updateStatus();
      this.querySelector('[data-dug-prev]').addEventListener('click', () => this._navigate(-1), { signal });
      this.querySelector('[data-dug-next]').addEventListener('click', () => this._navigate(1), { signal });
      this._toggle.addEventListener('click', () => {
        this._manualPaused = !this._manualPaused;
        this._keyboardFocus = false;
        this._syncRotation();
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this._hovered = true; this._syncRotation(); }
      }, { signal });
      this.addEventListener('pointerleave', () => { this._hovered = false; this._syncRotation(); }, { signal });
      this.addEventListener('pointerdown', () => { this._keyboardFocus = false; }, { signal });
      this.addEventListener('focusin', event => {
        this._keyboardFocus = event.target.matches(':focus-visible');
        this._syncRotation();
      }, { signal });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) { this._keyboardFocus = false; this._syncRotation(); }
      }, { signal });
      this._track.addEventListener('pointerdown', () => { this._dragging = true; this._syncRotation(); }, { signal });
      const finishDrag = () => {
        if (this._dragging) { this._dragging = false; this._syncRotation(); }
      };
      window.addEventListener('pointerup', finishDrag, { signal });
      window.addEventListener('pointercancel', finishDrag, { signal });
      this._track.addEventListener('keydown', event => {
        this._keyboardFocus = true;
        this._syncRotation();
        if (!this._usingSlick && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          this._navigate(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, { signal });
      this._track.addEventListener('scroll', () => {
        if (this._usingSlick) return;
        const cards = [...this._track.children];
        const origin = cards[0]?.offsetLeft || 0;
        const x = this._track.scrollLeft;
        let nearest = 0;
        cards.forEach((card, i) => {
          if (Math.abs(card.offsetLeft - origin - x) < Math.abs(cards[nearest].offsetLeft - origin - x)) nearest = i;
        });
        this._index = nearest;
        this._updateStatus();
      }, { passive: true, signal });
      document.addEventListener('visibilitychange', () => this._syncRotation(), { signal });
      this._motion.addEventListener('change', () => {
        if (this._motion.matches) this._manualPaused = true;
        if (this._usingSlick) this._jq.slick('slickSetOption', 'speed', this._motion.matches ? 0 : 650, false);
        this._syncRotation();
      }, { signal });
      if ('IntersectionObserver' in window) {
        this._visibilityObserver = new IntersectionObserver(entries => {
          this._visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.15);
          this._syncRotation();
        }, { threshold: [0, 0.15] });
        this._visibilityObserver.observe(this);
      }
      // Late/deferred vendor loading must not leave the carousel uninitialised.
      this._engineAttempts = 0;
      this._trySlick();
      this._syncRotation();
    }
    _trySlick() {
      if (!this.isConnected || this._usingSlick) return;
      const $ = window.jQuery;
      if (!$ || typeof $.fn.slick !== 'function') {
        if (++this._engineAttempts < 100) this._engineTimer = setTimeout(() => this._trySlick(), 200);
        return; // Native scrolling and automatic rotation remain usable.
      }
      const columns = Math.max(2, Math.min(5, Number(this.dataset.columns) || 4));
      this._jq = $(this._track);
      this._usingSlick = true;
      this._jq.on('init.dug setPosition.dug reInit.dug breakpoint.dug', () => this._queueMeasure());
      this._jq.on('afterChange.dug', (_event, slick, current) => {
        this._index = current || 0;
        this._count = slick.slideCount;
        this._updateStatus();
        this._syncRotation();
      });
      this._jq.on('swipe.dug', () => this._syncRotation());
      try {
        this._jq.slick({
          slidesToShow: columns, slidesToScroll: 1, initialSlide: this._index,
          infinite: true, rows: 0, autoplay: false,
          speed: this._motion.matches ? 0 : 650, cssEase: 'ease',
          arrows: false, dots: false, accessibility: true,
          swipe: true, swipeToSlide: true, draggable: true,
          pauseOnHover: false, pauseOnFocus: false, adaptiveHeight: false,
          responsive: [
            { breakpoint: 1200, settings: { slidesToShow: Math.min(columns, 3) } },
            { breakpoint: 980, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 1, centerMode: true, centerPadding: '24px' } }
          ]
        });
      } catch (error) {
        this._jq.off('.dug');
        this._usingSlick = false;
        console.warn('DUG testimonials: using native scrolling.', error);
      }
      this._queueMeasure();
      this._syncRotation();
    }
    _queueMeasure() {
      if (!this.isConnected) return;
      cancelAnimationFrame(this._measureFrame);
      this._measureFrame = requestAnimationFrame(() => this._measure());
    }
    _measure() {
      if (!this.isConnected || !this._track) return;
      const cards = [...this._track.querySelectorAll('.dug-t-card:not(.slick-cloned)')];
      if (!cards.length || cards[0].getBoundingClientRect().width < 1) return;
      // Measure unconstrained inner text, never the already equalised outer box.
      for (const [name, selector] of [
        ['--dug-quote-height', 'blockquote > p'],
        ['--dug-name-height', '.dug-t-name-line'],
        ['--dug-model-height', '.dug-t-model-label']
      ]) {
        const height = Math.ceil(Math.max(0, ...cards.map(card => card.querySelector(selector)?.getBoundingClientRect().height || 0)));
        const value = `${height}px`;
        if (this.style.getPropertyValue(name) !== value) this.style.setProperty(name, value);
      }
      this.dataset.layoutReady = 'true';
    }
    _navigate(direction) {
      if (!this.isConnected || this._count < 2) return;
      if (this._usingSlick) {
        this._jq.slick(direction < 0 ? 'slickPrev' : 'slickNext');
      } else {
        const cards = [...this._track.children];
        const max = Math.max(0, this._track.scrollWidth - this._track.clientWidth);
        let next = this._index + direction;
        if (direction > 0 && this._track.scrollLeft >= max - 2) next = 0;
        if (direction < 0 && this._track.scrollLeft <= 2) next = cards.length - 1;
        next = (next + cards.length) % cards.length;
        this._index = next;
        this._track.scrollTo({ left: cards[next].offsetLeft - cards[0].offsetLeft, behavior: this._motion.matches ? 'instant' : 'smooth' });
        this._updateStatus();
      }
      this._syncRotation();
    }
    _updateStatus() {
      if (this._status) this._status.textContent = `${this._index + 1} / ${this._count}`;
    }
    _syncRotation() {
      clearTimeout(this._rotationTimer);
      if (!this.isConnected || !this._toggle) return;
      const de = this.lang === 'de';
      const label = this._manualPaused
        ? (de ? 'Automatischen Wechsel starten' : 'Start automatic rotation')
        : (de ? 'Automatischen Wechsel pausieren' : 'Pause automatic rotation');
      this._toggle.classList.toggle('is-paused', this._manualPaused);
      this._toggle.setAttribute('aria-label', label);
      this._toggle.setAttribute('title', label);
      const running = !this._manualPaused && !this._hovered && !this._keyboardFocus && !this._dragging && this._visible && !document.hidden && this._count > 1;
      this.dataset.rotating = running ? 'true' : 'false';
      this._track.setAttribute('aria-live', 'off');
      if (running) this._rotationTimer = setTimeout(() => this._navigate(1), this._interval);
    }
    disconnectedCallback() {
      this._controller?.abort();
      this._widthObserver?.disconnect();
      this._visibilityObserver?.disconnect();
      cancelAnimationFrame(this._bootFrame);
      cancelAnimationFrame(this._measureFrame);
      clearTimeout(this._engineTimer);
      clearTimeout(this._rotationTimer);
      if (this._jq) {
        this._jq.off('.dug');
        if (this._jq.hasClass('slick-initialized')) this._jq.slick('unslick');
      }
      this._controller = null;
      this._ready = false;
      this._jq = null;
      this._usingSlick = false;
    }
  });
}
